import { useQuery, useMutation } from '@tanstack/react-query';
import { aiApi } from '@/lib/api';
import { useUIStore } from '@/stores/uiStore';
import type { AIResult } from '@/types';

// ─── Query keys ───────────────────────────────────────────────────────────────

export const aiKeys = {
  all: ['ai'] as const,
  semanticSearch: (workspaceId: string, query: string) =>
    [...aiKeys.all, 'search', workspaceId, query] as const,
  suggestions: (workspaceId: string, capabilityId: string) =>
    [...aiKeys.all, 'suggestions', workspaceId, capabilityId] as const,
  coaching: (workspaceId: string, capabilityIds: string[]) =>
    [...aiKeys.all, 'coaching', workspaceId, capabilityIds.sort().join(',')] as const,
};

// ─── Semantic search ──────────────────────────────────────────────────────────

export function useSemanticSearch(
  workspaceId: string | null,
  query: string,
  enabled = true
) {
  return useQuery({
    queryKey: aiKeys.semanticSearch(workspaceId ?? '', query),
    queryFn: async (): Promise<AIResult> => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return aiApi.semanticSearch(workspaceId, query);
    },
    enabled: enabled && Boolean(workspaceId) && query.trim().length > 2,
    staleTime: 60_000,
    gcTime: 5 * 60_000,
  });
}

// ─── Capability suggestions ───────────────────────────────────────────────────

export function useCapabilitySuggestions(
  workspaceId: string | null,
  capabilityId: string | null
) {
  return useQuery({
    queryKey: aiKeys.suggestions(workspaceId ?? '', capabilityId ?? ''),
    queryFn: async (): Promise<AIResult> => {
      if (!workspaceId || !capabilityId) {
        throw new Error('Missing workspaceId or capabilityId');
      }
      return aiApi.getSuggestions(workspaceId, capabilityId);
    },
    enabled: Boolean(workspaceId) && Boolean(capabilityId),
    staleTime: 5 * 60_000,
    gcTime: 10 * 60_000,
  });
}

// ─── Extract capabilities from freeform text ──────────────────────────────────

export function useExtractCapabilities(workspaceId: string | null) {
  const addToast = useUIStore((s) => s.addToast);

  return useMutation({
    mutationFn: async (text: string): Promise<AIResult> => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return aiApi.extractCapabilities(workspaceId, text);
    },
    onSuccess: (result) => {
      addToast({
        title: `Found ${result.results.length} capabilities`,
        description: 'Review and add them to your graph',
        variant: 'success',
      });
    },
    onError: (error: Error) => {
      addToast({
        title: 'Extraction failed',
        description: error.message,
        variant: 'error',
      });
    },
  });
}

// ─── Coaching insights ────────────────────────────────────────────────────────

export function useCoachingInsights(
  workspaceId: string | null,
  capabilityIds: string[]
) {
  return useQuery({
    queryKey: aiKeys.coaching(workspaceId ?? '', capabilityIds),
    queryFn: async (): Promise<AIResult> => {
      if (!workspaceId) {
        throw new Error('No workspace selected');
      }
      return aiApi.getCoaching(workspaceId, capabilityIds);
    },
    enabled: Boolean(workspaceId) && capabilityIds.length > 0,
    staleTime: 10 * 60_000,
    gcTime: 30 * 60_000,
  });
}
